import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, CardHeader, Divider, Grid, Typography,
  Stepper, Step, StepLabel, Button, Stack, IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import { UserType } from '../../../types/UserTypes';
import { Helmet } from 'react-helmet-async';
import LoadingSpinner from '../../../support/LoadingSpinner';
import axios from '../../../../services/ApiService';
import toast from 'react-hot-toast';
import { WorkflowTracker, Step as WorkflowStep, ActionType, WorkflowComment } from '../../../types/ProgramTypes';
import { useDocuments } from '../../../../hooks/useDocuments';
import { BVTConstants } from '../../../../utils/BVTConstants';
import { Document } from '../../../types/DocumentType';
import Swal from 'sweetalert2';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { AssessmentData } from '../../../types/AssessmentTypes';
import { useReports } from '../../../../hooks/useReports';
import PayPalButton from '../../../../services/PayPalService';

const WorkflowView = () => {
  const location = useLocation();
  const { assignmentId } = location.state as { assignmentId: number };
  const [loading, setLoading] = useState(false);
  const [workflow, setWorkflow] = useState<WorkflowTracker | null>(null);
  const { user } = useAuthContext();
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [comment, setComment] = useState('');
  const [currentStepTrackerId, setCurrentStepTrackerId] = useState<number | null>(null);
  const [savingComment, setSavingComment] = useState(false);
  const { showDocument, saveDocument } = useDocuments();
  const navigate = useNavigate();
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [documentData, setDocumentData] = useState<Document>({
    id: 0,
    fileName: '',
    purpose: 'WORKFLOW_DOCUMENT',
    contentFile: null,
    contentBytes: null,
    status: '',
    mimeType: '',
    createdAt: '',
    updatedAt: '',
  });
  const [openDocumentDialog, setOpenDocumentDialog] = useState(false);
  const [documentComment, setDocumentComment] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedTrackerId, setSelectedTrackerId] = useState<number | null>(null);
  const [growthChartData, setGrowthChartData] = useState<AssessmentData[]>([]);
  const { downloadReport } = useReports();

  useEffect(() => {
    if (assignmentId) {
      fetchWorkflowTracker();
    }
  }, [assignmentId]);

  const fetchWorkflowTracker = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/getWorkflowTrackersByAssignment', assignmentId);
      if (response.data) {
        setWorkflow(response.data);
        await populateGrowthChart(response.data);
      }
    } catch (error) {
      console.error('Error fetching workflows.');
    } finally {
      setLoading(false);
    }
  };

  const populateGrowthChart = async (workflow: WorkflowTracker) => {
    if (workflow.status !== 'COMPLETED') return;
    
    const assessmentSteps = workflow.workflowSteps.filter(
      step => step.stepActionType === BVTConstants.WORKFLOW_ACTION_TYPE_ASSESSMENT
    );
    
    if (assessmentSteps.length <= 1) return;

    try {
      const response = await axios.get(`/getBusinessAssessmentData/${workflow.assignmentId}`);
      setGrowthChartData(response.data);
    } catch (error) {
      console.error('Error fetching assessment data.');
    }
  };

  const getCurrentStageIndex = (workflow: WorkflowTracker) => {
    if (!workflow || !workflow.workflowSteps || !workflow.workflowStepTrackers) {
      return -1;
    }

    if (workflow.status === 'COMPLETED') {
      return workflow.workflowSteps.length;
    }

    const completedCount = workflow.workflowStepTrackers?.filter(t => t.status === 'COMPLETED')?.length || 0;
    return workflow.workflowStepTrackers.some(t => t.status === 'IN_PROGRESS') ? completedCount : -1;
  };

  const getStepStatus = (step: WorkflowStep) => {
    if (!workflow?.workflowStepTrackers) return 'PENDING';
    const tracker = workflow.workflowStepTrackers.find(t => t.stepId === step.id);
    return tracker?.status || 'PENDING';
  };

  const handleDownloadDocument = async (documentId: number) => {
    try {
      await showDocument(documentId);
    } catch (error) {
      console.error('Error downloading document.');
    }
  };

  const handleCompleteTaskClick = async (trackerId: number) => {
    Swal.fire({title: 'Complete Task ?', 
      text: 'Are you sure you want to complete the task ?', 
      icon: 'question', 
      confirmButtonText: 'Complete', 
      cancelButtonText: 'Cancel', 
      showCancelButton: true }
    ).then((result) => {
      if(result['isConfirmed']){
        handleCompleteTask(trackerId);
      }
    })
  };
  
  const handleCompleteTask = async (trackerId: number) => {
    try {
      setLoading(true);
      await axios.get(`/completeWorkflowTask/${trackerId}`);
      toast.success('Task completed successfully');
      await fetchWorkflowTracker();
    } catch (error) {
      console.error('Error completing task.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (trackerId: number) => {
    try {
      setLoading(true);
      await axios.get(`/sendWorkflowsReminder/${trackerId}`);
      toast.success('Reminder sent successfully');
      await fetchWorkflowTracker();
    } catch (error) {
      console.error('Error sending reminder.');
    } finally {
      setLoading(false);
    }
  };

  const handleCommentOpen = (trackerId: number) => {
    setCurrentStepTrackerId(trackerId);
    setOpenCommentDialog(true);
  };

  const handleCommentClose = () => {
    setOpenCommentDialog(false);
    setComment('');
    setCurrentStepTrackerId(null);
  };

  const handleCommentSubmit = async () => {
    try {
      setSavingComment(true);
      const commentData: WorkflowComment = {
        stepTrackerId: currentStepTrackerId!,
        commentByType: user?.role || '',
        comment: comment,
        commentId: 0,
        createdAt: ''
      };

      await axios.post('/saveWorkflowComment', commentData);
      toast.success('Comment added successfully');
      handleCommentClose();
      await fetchWorkflowTracker();
    } catch (error) {
      console.error('Error adding comment.');
    } finally {
      setSavingComment(false);
    }
  };

  const handlePaymentSuccess = async (tx: any) => {
    try {
      await fetchWorkflowTracker();
      toast.success('Payment completed successfully');
    } catch (error) {
      console.error('Error refreshing workflow after payment.');
    }
  };

  const handlePaymentError = (err: any) => {
    console.error('Payment error:', err);
    toast.error('Payment failed. Please try again.');
  };

  const handleDocumentDialogClose = () => {
    setOpenDocumentDialog(false);
    setDocumentComment('');
    setSelectedFile(null);
    setSelectedTrackerId(null);
  };

  const handleDocumentSubmit = async () => {
    if (!selectedFile || !selectedTrackerId) return;
    
    try {
      setUploadingDoc(true);
      const newDocData = {
        ...documentData,
        contentFile: selectedFile,
        fileName: selectedFile.name
      };
      
      const docId = await saveDocument(newDocData);
      
      await axios.post('/saveWorkflowDocument', {
        stepTrackerId: selectedTrackerId,
        documentId: docId,
        uploadedByType: user?.role || '',
        comment: documentComment,
      });
      
      toast.success('Document uploaded successfully');
      await fetchWorkflowTracker();
      handleDocumentDialogClose();
    } catch (error) {
      console.error('Error uploading document.');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleTakeAssessment = (trackerId: number) => {
    navigate(`/${user?.role === 'COMPANY' ? 'agent' : user?.role.toLowerCase()}/assessment/form`, { 
      state: { trackerId: trackerId } 
    });
  };

  const handleViewResult = (trackerId: number) => {
    navigate(`/${user?.role === 'COMPANY' ? 'agent' : user?.role.toLowerCase()}/assessment/details`, { 
      state: { trackerId: trackerId } 
    });
  };

  const getActionButtons = (userType: UserType, actionOn: UserType, actionType: ActionType, trackerId: number, step?: WorkflowStep) => {
    if(workflow?.status === BVTConstants.SUSPENDED) {
      //toast.error('Workflow is suspended. Please contact agent.');
      return [];
    }
    const buttons: JSX.Element[] = [];

    const addCommonButtons = () => {
      buttons.push(
        <Button 
          key="comment" 
          variant="outlined" 
          size="small"
          onClick={() => handleCommentOpen(trackerId)}
          sx={{ height: '36px' }}
        >
          Add Comment
        </Button>
      );
    };

    // Admin user logic
    if (userType === 'ADMIN') {
      if (actionOn === 'ADMIN') {
        addCommonButtons();
        if (actionType === BVTConstants.WORKFLOW_ACTION_TYPE_ASSESSMENT) {
          buttons.push(
            <Button 
              key="assess" 
              variant="contained" 
              size="small"
              onClick={() => handleTakeAssessment(trackerId)}
            >
              Take Assessment
            </Button>
          );
        } else if (actionType === BVTConstants.WORKFLOW_ACTION_TYPE_CUSTOM) {
          buttons.push(
            <Button 
              key="complete" 
              variant="contained" 
              size="small"
              onClick={() => handleCompleteTaskClick(trackerId)}
            >
              Complete Task
            </Button>
          );
        }
      } else {
        buttons.push(
          <Button 
            key="reminder" 
            variant="contained" 
            size="small"
            onClick={() => handleSendReminder(trackerId)}
          >
            Send Reminder
          </Button>
        );
      }
    }

    // Agent user logic
    else if (userType === 'AGENT' || userType === 'COMPANY') {
      if (actionOn === 'AGENT') {
        addCommonButtons();
        if (actionType === BVTConstants.WORKFLOW_ACTION_TYPE_ASSESSMENT) {
          buttons.push(
            <Button 
              key="assess" 
              variant="contained" 
              size="small"
              onClick={() => handleTakeAssessment(trackerId)}
            >
              Take Assessment
            </Button>
          );
        } else if (actionType === BVTConstants.WORKFLOW_ACTION_TYPE_PAYMENT) {
          buttons.push(
            <PayPalButton
              key="payment"
              cart={{ 
                type: BVTConstants.WORKFLOW_PAYMENT, 
                refId: trackerId, 
                amount: step?.cost || 0
              }}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
          );
        } else {
          buttons.push(
            <Button 
              key="complete" 
              variant="contained" 
              size="small"
              onClick={() => handleCompleteTaskClick(trackerId)}
            >
              Complete Task
            </Button>
          );
        }
      } else {
        // Add PayPal button for agents when action is payment, regardless of actionOn
        if (actionType === BVTConstants.WORKFLOW_ACTION_TYPE_PAYMENT) {
          buttons.push(
            <PayPalButton
              key="payment"
              cart={{ 
                type: BVTConstants.WORKFLOW_PAYMENT, 
                refId: trackerId, 
                amount: step?.cost || 0
              }}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
          );
        }
        buttons.push(
          <Button 
            key="reminder" 
            variant="contained" 
            size="small"
            onClick={() => handleSendReminder(trackerId)}
          >
            Send Reminder
          </Button>
        );
      }
    }

    // Business user logic
    else if (userType === 'BUSINESS') {
      if (actionOn === 'BUSINESS') {
        addCommonButtons();
        if (actionType === BVTConstants.WORKFLOW_ACTION_TYPE_ASSESSMENT) {  
          buttons.push(
            <Button 
              key="assess" 
              variant="contained" 
              size="small"
              onClick={() => handleTakeAssessment(trackerId)}
            >
              Take Assessment
            </Button>
          );
        } else if (actionType === BVTConstants.WORKFLOW_ACTION_TYPE_PAYMENT) {
          buttons.push(
            <PayPalButton
              key="payment"
              cart={{ 
                type: BVTConstants.WORKFLOW_PAYMENT, 
                refId: trackerId, 
                amount: step?.cost || 0
              }}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
          );
        } else {
          buttons.push(
            <Button 
              key="complete" 
              variant="contained" 
              size="small"
              onClick={() => handleCompleteTaskClick(trackerId)}
            >
              Complete Task
            </Button>
          );
        }
      } else if (actionOn === 'AGENT') {
        addCommonButtons();
        buttons.push(
          <Button 
            key="reminder" 
            variant="contained" 
            size="small"
            onClick={() => handleSendReminder(trackerId)}
          >
            Send Reminder
          </Button>
        );
      }
    }

    if ((userType === 'AGENT' || userType === 'COMPANY' || userType === 'BUSINESS') && 
        actionType === BVTConstants.WORKFLOW_ACTION_TYPE_CUSTOM) {
      buttons.push(
        <Button
          key="upload"
          variant="contained"
          component="label"
          size="small"
          disabled={uploadingDoc}
        >
          {uploadingDoc ? 'Uploading...' : 'Upload Document'}
          <input
            type="file"
            hidden
            accept={BVTConstants.FILE_TYPES}
            onChange={(event) => {
              if (event.target.files && event.target.files.length > 0) {
                const file = event.target.files[0];
                
                // Check file size
                if (file.size > BVTConstants.FILE_SIZE_1_MB_LIMIT) {
                  toast.error('File size should not exceed 1 MB');
                  return;
                }
                
                // Check file format
                const allowedTypes = BVTConstants.FILE_TYPES.split(',');
                const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
                const isValidFormat = allowedTypes.some(type => 
                  type.toLowerCase() === fileExtension
                );
                
                if (!isValidFormat) {
                  toast.error(`Invalid file format. Allowed formats: ${BVTConstants.FILE_TYPES}`);
                  return;
                }
                
                setSelectedFile(file);
                setSelectedTrackerId(trackerId);
                setOpenDocumentDialog(true);
              }
            }}
          />
        </Button>
      );
    }

    return buttons;
    
  };

  const getCurrentTask = () => {
    if (!workflow) return null;
    
    const currentTracker = workflow.workflowStepTrackers.find(t => t.status === 'IN_PROGRESS');
    if (!currentTracker) return null;
    
    return workflow.workflowSteps.find(step => step.id === currentTracker.stepId);
  };

  if (loading) return <LoadingSpinner loading={loading} />;
  if (!workflow || !user) return null;

  const sortedSteps = workflow.workflowSteps ? 
    [...workflow.workflowSteps].sort((a, b) => a.seqId - b.seqId) : [];
  const currentStageIndex = getCurrentStageIndex(workflow);

  return (
    <>
      <Helmet>
        <title>BVT - Workflow Details</title>
      </Helmet>
      <Box sx={{ padding: 2 }}>
        <Card sx={{ mb: 3, position: 'relative' }}>
          <IconButton 
            onClick={() => navigate(-1)}
            sx={{ 
              position: 'absolute',
              right: 8,
              top: 8,
              bgcolor: 'background.paper',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">{workflow.workflowName}</Typography>
                {workflow.status === BVTConstants.SUSPENDED && (
                  <Tooltip title="Workflow is suspended, contact agent" arrow>
                    <Typography
                      variant="caption"
                      sx={{
                        backgroundColor: '#ff9800',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        cursor: 'help'
                      }}
                    >
                      SUSPENDED
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            }
            subheader={workflow.programName}
            sx={{ backgroundColor: '#f5f5f5', pr: 6 }}
          />
          <Divider />
          <CardContent>
            <Typography variant="h6" marginBottom="15px">Workflow Progress</Typography>
            <Stepper alternativeLabel activeStep={currentStageIndex}>
              {sortedSteps.map((step, index) => (
                <Step key={step.id} completed={workflow.status === 'COMPLETED' || index < currentStageIndex}>
                  <StepLabel
                    StepIconComponent={(props) => (
                      workflow.status === 'COMPLETED' || index < currentStageIndex ? (
                        <CheckCircleIcon color="success" />
                      ) : index === currentStageIndex ? (
                        <RadioButtonUncheckedIcon color="primary" />
                      ) : (
                        <RadioButtonUncheckedIcon color="disabled" />
                      )
                    )}
                  >
                    {step.stepName}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
          {workflow.status !== 'COMPLETED' && (
            <>
              <Divider />
              <CardContent>
                <Typography variant="h6" marginBottom="15px">Current Task</Typography>
                {sortedSteps.map((step) => {
                  const stepTracker = workflow.workflowStepTrackers.find(t => t.stepId === step.id);
                  if (stepTracker?.status === 'IN_PROGRESS') {
                    console.log('Found in-progress step:', step); // Debug log
                    return (
                      <React.Fragment key={step.id}>
                        <Grid container spacing={3}>
                          <Grid item xs={4}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              Action
                            </Typography>
                            <Typography variant="body2">
                              {step.stepName}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              Instructions
                            </Typography>
                            <Typography variant="body2">
                              {step.instructions}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              Assigned To
                            </Typography>
                            <Typography variant="body2">
                              {step.stepActionOn}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
                          {getActionButtons(
                            user.role as UserType,
                            step.stepActionOn as UserType,
                            step.stepActionType as ActionType,
                            stepTracker.stepTrackerId,
                            step
                          )}
                        </Box>
                      </React.Fragment>
                    );
                  }
                  return null;
                })}
              </CardContent>
            </>
          )}
          <Divider />
          <CardContent sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              {workflow.status === 'COMPLETED' ? 'Completed On: ' : 'Last Updated: '}{workflow.updatedAt}
            </Typography>
          </CardContent>
        </Card>

        {workflow.status === 'COMPLETED' && growthChartData.length > 1 && (
          <Card sx={{ mb: 3 }}>
            <CardHeader
              title="Growth Chart"
              sx={{ backgroundColor: '#f5f5f5' }}
            />
            <Divider />
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                overflowX: 'auto',
                py: 2,
                minWidth: 'min-content'
              }}>
                {growthChartData
                  .sort((a, b) => new Date(a.completedDate || '').getTime() - new Date(b.completedDate || '').getTime())
                  .map((assessment, index, arr) => (
                    <React.Fragment key={assessment.dataId}>
                      <Box sx={{ 
                        textAlign: 'center',
                        flex: 1,
                        width: `${100 / growthChartData.length}%`,  // Dynamic width
                        minWidth: '200px',  // Minimum width to prevent too narrow boxes
                        maxWidth: '400px',  // Maximum width to prevent too wide boxes
                        p: 2,
                        bgcolor: '#f8f8f8',
                        borderRadius: 1
                      }}>
                        <Typography variant="subtitle1" gutterBottom>
                          {assessment.assessmentName}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {assessment.scoredPoints}/{assessment.totalScore}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {assessment.result}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Completed on: {new Date(assessment.completedDate || '').toLocaleDateString()}
                        </Typography>
                      </Box>
                      {index < arr.length - 1 && (
                        <Box sx={{ textAlign: 'center' }}>
                          <ArrowRightAltIcon 
                            sx={{ 
                              color: 'primary.main',
                              fontSize: 40
                            }} 
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {Math.ceil(
                              (new Date(arr[index + 1].completedDate || '').getTime() - 
                              new Date(assessment.completedDate || '').getTime()) / 
                              (1000 * 60 * 60 * 24)
                            )} days
                          </Typography>
                        </Box>
                      )}
                    </React.Fragment>
                  ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Add Tasks header section */}
        <Box sx={{ mb: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Tasks
          </Typography>
        </Box>

        {sortedSteps
          .filter(step => {
            const stepTracker = workflow?.workflowStepTrackers?.find(t => t.stepId === step.id);
            return stepTracker?.status === 'IN_PROGRESS' || stepTracker?.status === 'COMPLETED';
          })
          .sort((a, b) => {
            const aTracker = workflow?.workflowStepTrackers?.find(t => t.stepId === a.id);
            const bTracker = workflow?.workflowStepTrackers?.find(t => t.stepId === b.id);
            
            // In Progress tasks always come first
            if (aTracker?.status === 'IN_PROGRESS') return -1;
            if (bTracker?.status === 'IN_PROGRESS') return 1;
            
            // For completed tasks, sort by completion time (updatedAt) descending
            const aTime = new Date(aTracker?.updatedAt || '').getTime();
            const bTime = new Date(bTracker?.updatedAt || '').getTime();
            return bTime - aTime;
          })
          .map((step) => {
            const stepStatus = getStepStatus(step);
            const stepTracker = workflow?.workflowStepTrackers?.find(t => t.stepId === step.id);
            const stepComments = stepTracker?.workflowComments || [];
            const stepDocuments = stepTracker?.workflowDocuments || [];

            return (
              <Card key={step.id} sx={{ mb: 2 }}>
                <CardHeader
                  title={step.stepName}
                  subheader={`Status: ${stepStatus}`}
                  sx={{ backgroundColor: '#f5f5f5' }}
                  action={
                    step.stepActionType === BVTConstants.WORKFLOW_ACTION_TYPE_ASSESSMENT && 
                    stepStatus === 'COMPLETED' && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleViewResult(stepTracker?.stepTrackerId || 0)}
                        >
                          View Result
                        </Button>
                        {(user.role === 'ADMIN' || user.role === 'COMPANY') && step.stepActionOn === 'BUSINESS' && (
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => downloadReport(
                              '/generateAssessmentCertificate',
                              stepTracker?.stepTrackerId,
                              `${step.stepName}_Certificate.pdf`
                            )}
                          >
                            Download Certificate
                          </Button>
                        )}
                      </Box>
                    )
                  }
                />
                <Divider />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Action
                      </Typography>
                      <Typography variant="body2">
                        {step.stepActionType}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Instructions
                      </Typography>
                      <Typography variant="body2">
                        {step.instructions}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Assigned To
                      </Typography>
                      <Typography variant="body2">
                        {step.stepActionOn}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>Comments</Typography>
                    <Stack spacing={2}>
                      {stepComments.length > 0 ? (
                        stepComments.map((comment) => (
                          <Box key={comment.commentId} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="primary">
                              {comment.commentByType}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {comment.comment}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {comment.createdAt}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            No comments available
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>Documents</Typography>
                    <Stack spacing={2}>
                      {stepDocuments.length > 0 ? (
                        stepDocuments.map((doc) => (
                          <Box key={doc.wdId} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box>
                                <Typography variant="subtitle2" color="primary">
                                  Uploaded by {doc.uploadedByType}
                                </Typography>
                                {doc.comment && (
                                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                    {doc.comment}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                  {doc.createdAt}
                                </Typography>
                              </Box>
                              <Button
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadDocument(doc.documentId)}
                              >
                                Download
                              </Button>
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            No documents available
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" color="textSecondary">
                      {stepStatus === 'COMPLETED' ? 'Completed On: ' : 'Last Updated: '}
                      {stepTracker?.updatedAt}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
      </Box>
      <LoadingSpinner loading={loading} />
      <Dialog 
        open={openCommentDialog} 
        onClose={handleCommentClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '300px'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          Add Comment
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Comment"
            fullWidth
            multiline
            rows={6}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            inputProps={{ maxLength: 150 }}
            helperText={`${comment.length}/150 characters`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button 
            onClick={handleCommentClose} 
            variant="outlined"
            disabled={savingComment}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCommentSubmit}
            disabled={!comment.trim() || savingComment}
            variant="contained"
          >
            {savingComment ? 'Saving...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog 
        open={openDocumentDialog} 
        onClose={handleDocumentDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '300px'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          Upload Document
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Selected file: {selectedFile?.name}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Comment"
            fullWidth
            multiline
            rows={4}
            value={documentComment}
            onChange={(e) => setDocumentComment(e.target.value)}
            inputProps={{ maxLength: 150 }}
            helperText={`${documentComment.length}/150 characters`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button 
            onClick={handleDocumentDialogClose} 
            variant="outlined"
            disabled={uploadingDoc}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDocumentSubmit}
            disabled={uploadingDoc}
            variant="contained"
          >
            {uploadingDoc ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WorkflowView; 